import { MoreDotsIcon } from '@tableau/icons/MoreDotsIcon';
import { UsersGroupIcon } from '@tableau/icons/UsersGroupIcon';
import { DropdownButton, DropdownButtonType, List, ListItem, Tooltip } from '@tableau/widgets';
import { FormikContextType, useField } from 'formik';
import React, { ReactElement, useRef, useState } from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { IGroupBasicInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { AgentBetaWarningDialog } from './AgentBetaWarningDialog';
import { AgentGroupPermissionsDialog } from './AgentGroupPermissionsDialog';
import { AgentRowStatusMessage } from './AgentRowStatusMessage';
import {styles } from './AgentSectionStyles';
import { _groupName, agentSupportsGroups, getAgentAvailabilityInfo, getAgentFieldPath, getAgentLabel, getTestId } from './AgentSectionsUtils';
import { Agent, AgentPerm, AgentPermissionsSectionProps } from './AgentSectionTypes';

interface AgentRowProps {
    agent: Agent;
    allowTestRegistration: boolean;
    allUsersGroup: IGroupBasicInfo;
    formik: FormikContextType<AgentPermissionsSectionProps>;
    groups: IGroupBasicInfo[];
    isTrial: boolean;
    isTryItMode: boolean;
    registered: boolean;
    tableauAITrial?: ISiteTrial;
}

interface AgentActionMenuProps {
    agent: Agent;
    agentLabel: string;
    closeDropdown: () => void;
    toggleGroupDialog: (state?: boolean) => void;
    setAgentPermission: {
       enabled: (e: boolean) => void;
       groupId: (g: string) => void;
    };
    allUsersGroup: IGroupBasicInfo;
    openBetaDialog: () => void;
}

export const ActionMenuOptions = (agent: Agent): ListItem[] => {
    const options: ListItem[] =  [
    { value: agent.enabled ? 'deactivate' : 'activate',
      content: agent.enabled ? Messages.settings_agents_state_deactivate() : Messages.settings_agents_state_activate(),
      testId: getTestId('ActionMenuOption_toggle', agent.id)
    }];
    if (agentSupportsGroups(agent)) options.push({
            value: 'grantAccess',
            content: Messages.settings_agents_grant_access_to(),
            disabled: !agent.enabled,
            testId: getTestId('ActionMenuOption_grantAccess', agent.id)});
    return options;
};


export const AgentActionsMenu = ({agent, agentLabel, closeDropdown, setAgentPermission, toggleGroupDialog, allUsersGroup, openBetaDialog}: AgentActionMenuProps) => {
    const ref = useRef<List>(null);
    const handleClick = (item: string): void => {
        switch (item) {
            case 'activate': {
                if (!agent.betaFlag || !FeatureFlags[agent.betaFlag]) {
                    setAgentPermission.enabled(true);
                    break;
                }
                openBetaDialog();
            }
            case 'deactivate': {
                setAgentPermission.enabled(false);
                break;
            }
            case 'grantAccess': {
                toggleGroupDialog(true);
                break;
            }
        }
        ref.current.setActiveItemIndex(-1);
        closeDropdown();
    };
    return <List
        ref={ref} role="menu"
        items={ActionMenuOptions(agent)}
        id={`Action Menu for ${agentLabel}`}
        onSelect={handleClick}
        selectedValue=""
        testId={getTestId('AgentActionsMenu', agent.id)}
    />;
};

interface AgentActionButtonProps  {
    agent: Agent;
    agentLabel: string;
    toggleGroupDialog: (state?: boolean) => void;
    disabled: boolean;
    setAgentPermission: {
       enabled: (e: boolean) => void;
       groupId: (g: string) => void;
    };
    allUsersGroup: IGroupBasicInfo;
    openBetaDialog: () => void;
}

export const ActionButton = ({agent, agentLabel, toggleGroupDialog, disabled, setAgentPermission, allUsersGroup, openBetaDialog}: AgentActionButtonProps) => {
    const ref = useRef<DropdownButton>(null);
    const close = () => {
        ref.current?.toggle(false);
    };
    return <DropdownButton
                aria-controls={`Action Menu for ${agentLabel}`}
                label=""
                type={DropdownButtonType.Icon}
                popupContent={<AgentActionsMenu
                                    agent={agent}
                                    agentLabel={agentLabel}
                                    closeDropdown={close}
                                    toggleGroupDialog={toggleGroupDialog}
                                    setAgentPermission={setAgentPermission}
                                    allUsersGroup={allUsersGroup}
                                    openBetaDialog={openBetaDialog}
                                />
                            }
                ref={ref}
                focusOnClose={true}
                disabled={disabled}
                testId={getTestId('ActionButton', agent.id)}
                className={styles.actionButton}
            >
                <MoreDotsIcon size={14} />
            </DropdownButton>;
};

export const AgentRow = ({
    agent,
    allowTestRegistration,
    allUsersGroup,
    formik,
    groups,
    isTrial,
    isTryItMode,
    registered,
    tableauAITrial
}: AgentRowProps): ReactElement => {
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [betaDialogOpen, setBetaDialogOpen] = useState(false);
    const groupName = _groupName(groups);
    const agentLabel = getAgentLabel(agent, isTrial);
    const [enabled] = useField({name: getAgentFieldPath(agent, AgentPerm.Enabled), type: 'checkbox'});
    const [groupId] = useField({name: getAgentFieldPath(agent, AgentPerm.GroupId), type: 'text'});
    const { actionButtonDisabled, isAvailable } = getAgentAvailabilityInfo({ agent, allowTestRegistration, isTrial, isTryItMode, registered, tableauAITrial });
    const groupsNotAllowed = agent.dashboardQAFlag && FeatureFlags[agent.dashboardQAFlag];
    const isTryItModeWithNonActiveTrialAndAgentRequiringRegistration = isTryItMode && tableauAITrial?.status !== TrialStatus.ACTIVE && agent.requiresRegistration;
    const readyForGroups = agentSupportsGroups(agent)
                            && isAvailable
                            && enabled.value
                            && !isTryItModeWithNonActiveTrialAndAgentRequiringRegistration;

    const openBetaDialog = () => setBetaDialogOpen(true);

    if (agent.flag && !FeatureFlags[agent.flag]) return null;

    agent.groupId = groupId.value ?? null;
    agent.enabled = enabled.value;

    const setAgentPermission = {
        enabled: (enabled: boolean) => formik.setFieldValue(getAgentFieldPath(agent, AgentPerm.Enabled), enabled),
        groupId: (gid: string) => formik.setFieldValue(getAgentFieldPath(agent, AgentPerm.GroupId), gid),
    };

    const toggleGroupDialog = (state?: boolean) => {
        setGroupDialogOpen(state ?? !groupDialogOpen);
    };

    const toggleBetaDialog = (state?: boolean) => {
        setBetaDialogOpen(state ?? !betaDialogOpen);
    };

    let groupText: React.ReactChild;
    const canShowGroup = groupId.value && (!agent.readyForGroupsFlag || FeatureFlags[agent.readyForGroupsFlag]);
    if (!enabled.value || !isAvailable || isTryItModeWithNonActiveTrialAndAgentRequiringRegistration) {
        groupText = Messages.settings_agents_access_group_none();
    } else if (groupsNotAllowed || !canShowGroup) {
        groupText = Messages.settings_agents_access_group_all_users();
    } else {
        groupText = <span>{groupName(groupId.value)}</span>;
    }

    const showGroupIcon = readyForGroups && groupId.value && groupId.value !== allUsersGroup?.groupLuid;

    return(
        <div data-tb-test-id={getTestId('AgentRow', agent.id)} className={classes(styles.row, styles.bodyRow)}>
            <div className={classes(styles.agentNameCell, styles.cell)}>
                <Tooltip  afterDelay={true} tooltipContent={agentLabel} followCursor={true} testId={getTestId('StatusMessage_Tooltip', agent.id)}>
                    <span className={styles.nameSpan}>{agentLabel}</span>
                </Tooltip>
            </div>
            <div className={classes(styles.cell, styles.status)}>
                <AgentRowStatusMessage
                    agent={agent}
                    isAvailable={isAvailable}
                    isTryItMode={isTryItMode}
                    tableauAITrial={tableauAITrial}
                />
            </div>
            <div className={classes(styles.cell, styles.group)} data-tb-test-id={getTestId('AgentRow_access_group', agent.id)}>
                <Tooltip
                    afterDelay={true} tooltipContent={groupText}
                    followCursor={true}
                    testId={getTestId('GroupMessage_Tooltip', agent.id)}>
                        <span className={styles.hasIcon}>{ showGroupIcon && <UsersGroupIcon
                                size={14}
                                className={styles.icon}/>}{groupText}
                        </span>
                </Tooltip>
            </div>
            <div className={classes(styles.cell, styles.actions)}>
                <ActionButton
                    agent={agent}
                    agentLabel={agentLabel}
                    toggleGroupDialog={toggleGroupDialog}
                    disabled={actionButtonDisabled}
                    setAgentPermission={setAgentPermission}
                    allUsersGroup={allUsersGroup}
                    openBetaDialog={openBetaDialog}
                />
                {groupDialogOpen && <AgentGroupPermissionsDialog
                    agent={agent}
                    agentLabel={agentLabel}
                    groups={groups}
                    handleClose={() => toggleGroupDialog(false)}
                    chooseGroup={setAgentPermission.groupId}
                    />}
                {betaDialogOpen && <AgentBetaWarningDialog
                    agent={agent}
                    groups={groups}
                    handleClose={() => toggleBetaDialog(false)}
                    activate={() => setAgentPermission.enabled(true)}
                    />}
            </div>
        </div>);
};
